import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {addBreadcrumb} from '@sentry/browser';

// eslint-disable-next-line no-restricted-imports
import {not, path} from 'ramda';
import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {browserStorageKey} from '@dms/config';
import {getWorkspaceFromUri} from '@dms/routes';

import {BaseQueryExtraOptions, parseZodSchema, ParseZodSchemaQueryInfo} from 'shared/web';

import {captureBaseQueryError} from './captureBaseQueryError';
import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';
import {refreshAccessToken} from './refreshAccessToken';
import {handleClientNotification} from './utils/handleClientNotification';

const RETRY_DELAY = 2000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];

const getHeaderValue = (headers: FetchArgs['headers'], key: string): string | undefined => {
  if (headers instanceof Headers) {
    return headers.get(key) ?? undefined;
  }
  if (Array.isArray(headers)) {
    return headers.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];
  }
  if (headers) {
    return headers[key] as string | undefined;
  }
  return undefined;
};

const getArgsWithAuthorization = (args: FetchArgs, branch?: string) => {
  const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
  const {workspace} = getWorkspaceFromUri();

  const explicitBranch =
    getHeaderValue(args.headers, 'x-branch') ?? getHeaderValue(args.headers, 'X-Branch');
  const hasExplicitBranch = isNotNilOrEmpty(explicitBranch);

  return {
    ...args,
    headers: {
      ...args.headers,
      credentials: 'include',
      'accept-language': localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE) || 'en',
      ...(accessToken ? {authorization: `Bearer ${accessToken}`} : {}),
      // TODO - Blocked by CORS on METADA
      // 'x-country-format': 'iso', // https://carvago.slack.com/archives/G01AN5XCXGX/p1634202539053900
      ...(!hasExplicitBranch && branch ? {'X-Branch': branch} : {}),
      ...(workspace ? {'X-Workspace': workspace} : {}),
    },
  };
};

const fetchBaseQueryInstance = fetchBaseQuery({baseUrl: process.env.METADA_URL});

export interface MetadaCustomFetchArgs extends FetchArgs {
  queryArg?: Record<string, unknown> | void;
}

export const metadaBaseQuery: BaseQueryFn<
  MetadaCustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const activeBranchId = path<string>(['branch', 'activeBranchId'], api.getState());

  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.queryArg,
      schema: extraOptions.requestSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'request',
      },
    });
  }

  let result = await fetchBaseQueryInstance(
    getArgsWithAuthorization(args, activeBranchId),
    api,
    extraOptions
  );

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'response',
      },
    });
  }

  /**
   * This retry logic is used to handle infrastructure errors. This type of errors are often ad-hoc
   * and not related to the request payload, so retrying the request should help.
   * https://carvago.atlassian.net/browse/T20-80058
   */
  if (result.error && !extraOptions?.skipInfrastructureRetry) {
    if (
      INFRASTRUCTURE_ERRORS_CODES.includes(
        Number(result.meta?.response?.status ?? result.error.status)
      )
    ) {
      addBreadcrumb({
        category: 'api',
        message: `Retrying metada API request ${args.url}, because response status is ${result.meta?.response?.status ?? result.error.status}`,
        level: 'error',
      });
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      result = await fetchBaseQueryInstance(
        getArgsWithAuthorization(args, activeBranchId),
        api,
        extraOptions
      );
    }
  }

  if (result.error && typeof result.error.status === 'number' && result.error.status >= 500) {
    captureBaseQueryError(result.error, args, api);
  }

  if (
    result.error &&
    (result.error.status === 401 ||
      (isString(result?.error?.data) && result?.error?.data.includes('authorization')) ||
      (isString(result?.error?.data) && result?.error?.data.includes('Unauthorized')))
  ) {
    const refreshed = await refreshAccessToken(api.dispatch, api.getState);

    if (refreshed) {
      result = await fetchBaseQueryInstance(
        getArgsWithAuthorization(args, activeBranchId),
        api,
        extraOptions
      );
    }
  }

  // Status 299 indicates an issue with the client's payload without blocking the flow.
  // Message from BE should be displayed to the client.
  if (result.meta?.response?.status === 299) {
    handleClientNotification(result.meta?.response?.headers);
  }

  return result;
};
